import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Button, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import { HEADING_FONT } from "../theme/fonts";
import { COLORS } from "../theme/tokens";
import { useToast } from "../components/ToastProvider";
import UserFormBody from "../components/UserFormBody";

// Thin /users/new and /users/:id/edit page wrapper. The form lives in the shared
// UserFormBody component (also used by the Add/Edit User modal on the Users
// list). Kept so direct links / bookmarks still work.
function UserForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const isEdit = !!id;
  const goBack = () => navigate("/users");

  return (
    <DashboardLayout>
      <Button
        onClick={goBack}
        startIcon={<ArrowBackIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          color: COLORS.muted,
          mb: 2,
          ml: -1,
          "&:hover": { background: COLORS.tile, color: COLORS.ink },
        }}
      >
        Back to Users
      </Button>

      <Box
        sx={{
          background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 60%, #60A5FA 100%)`,
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          mb: 3,
          color: "#fff",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ManageAccountsOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                opacity: 0.85,
              }}
            >
              {isEdit ? "Edit User" : "Create User"}
            </Typography>
            <Typography
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: { xs: 24, md: 32 },
                fontWeight: 700,
                letterSpacing: "-0.5px",
                lineHeight: 1.1,
              }}
            >
              {isEdit ? "Update User" : "New User"}
            </Typography>
            <Typography sx={{ fontSize: 13, opacity: 0.85, mt: 0.5 }}>
              {isEdit
                ? "Update credentials, role and module access"
                : "Add a new account and choose what it can access"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <UserFormBody
        userId={id || null}
        onCancel={goBack}
        onSuccess={({ isEdit: edited }) => {
          toast.success(edited ? "User updated" : "User created");
          goBack();
        }}
      />
    </DashboardLayout>
  );
}

export default UserForm;
